/**
 * 바늘땀 미리보기 (Canvas 2D). 실제 박히는 순서대로 한 땀씩 그림자·몸통·광택 3겹으로 그려 실 질감을 낸다.
 * 앱과 샘플 PNG 생성 스크립트가 같은 코드를 쓴다.
 */
export interface PlanLike {
  xs: ArrayLike<number>;
  ys: ArrayLike<number>;
  cmds: ArrayLike<number>;
  kinds: ArrayLike<number>;
}

export interface View {
  /** 화면 px / mm */
  scale: number;
  /** 디자인 (0,0) 이 올 화면 위치(px) */
  cx: number;
  cy: number;
}

export interface RenderOpts {
  color: string;
  fabric: string;
  showJumps: boolean;
  /** 이 인덱스까지만 (봉제 순서 재생) */
  upto?: number;
  mode: "thread" | "path" | "order";
  /** 언더레이를 흐리게 보여 줄지 (경로 모드) */
  dpr?: number;
}

const STITCH = 0,
  JUMP = 1,
  TRIM = 2;

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const v = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0").slice(0, 6);
  const n = parseInt(v, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mix(c: [number, number, number], t: [number, number, number], a: number): string {
  const r = Math.round(c[0] + (t[0] - c[0]) * a),
    g = Math.round(c[1] + (t[1] - c[1]) * a),
    b = Math.round(c[2] + (t[2] - c[2]) * a);
  return `rgb(${r},${g},${b})`;
}

/** 실 색이 원단과 비슷하면 윤곽이 안 보이므로 그림자를 조금 진하게 */
function luminance([r, g, b]: [number, number, number]) {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

export function renderPlan(ctx: CanvasRenderingContext2D, plan: PlanLike, view: View, o: RenderOpts) {
  const W = ctx.canvas.width,
    H = ctx.canvas.height;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = o.fabric;
  ctx.fillRect(0, 0, W, H);
  drawWeave(ctx, W, H, o.fabric, view.scale);
  const base = hexToRgb(o.color);
  const dark = mix(base, [0, 0, 0], 0.45);
  const body = mix(base, [255, 255, 255], 0.04);
  const hi = mix(base, [255, 255, 255], luminance(base) > 0.75 ? 0.9 : 0.55);
  const s = view.scale / 10; // 0.1mm 단위 → px
  const X = (v: number) => view.cx + v * s;
  const Y = (v: number) => view.cy + v * s;
  const n = Math.min(plan.cmds.length, o.upto ?? plan.cmds.length);
  const thread = 0.38 * view.scale;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  let px = 0,
    py = 0;
  if (o.mode === "order") {
    // 개발용: 바느질 순서를 색(파랑→빨강)으로
    ctx.lineWidth = Math.max(0.8, 0.08 * view.scale);
    for (let i = 0; i < n; i++) {
      const c = plan.cmds[i],
        x = plan.xs[i],
        y = plan.ys[i];
      if (c === STITCH) {
        const h = Math.round((1 - i / n) * 240);
        const k = plan.kinds[i];
        ctx.strokeStyle = `hsla(${h},85%,${k === 1 ? 70 : k === 2 ? 30 : 45}%,${k === 1 ? 0.7 : 1})`;
        ctx.lineWidth = Math.max(0.8, (k === 2 ? 0.14 : 0.07) * view.scale);
        ctx.beginPath();
        ctx.moveTo(X(px), Y(py));
        ctx.lineTo(X(x), Y(y));
        ctx.stroke();
      } else if (c === JUMP) drawJump(ctx, X(px), Y(py), X(x), Y(y), view.scale);
      px = x;
      py = y;
    }
    ctx.restore();
    return;
  }
  if (o.mode === "path") {
    ctx.lineWidth = Math.max(0.6, 0.06 * view.scale);
    for (let i = 0; i < n; i++) {
      const c = plan.cmds[i],
        x = plan.xs[i],
        y = plan.ys[i];
      if (c === STITCH) {
        const k = plan.kinds[i];
        ctx.strokeStyle = k === 1 ? "rgba(214,120,40,.85)" : k === 2 || k === 3 ? "rgba(60,140,90,.9)" : body;
        ctx.beginPath();
        ctx.moveTo(X(px), Y(py));
        ctx.lineTo(X(x), Y(y));
        ctx.stroke();
        if (view.scale > 6) {
          ctx.fillStyle = "rgba(20,20,20,.55)";
          ctx.fillRect(X(x) - 1, Y(y) - 1, 2, 2);
        }
      } else if (c === JUMP && o.showJumps) drawJump(ctx, X(px), Y(py), X(x), Y(y), view.scale);
      px = x;
      py = y;
    }
    ctx.restore();
    return;
  }
  const off = 0.045 * view.scale;
  const shadowA = luminance(base) > 0.7 ? 0.3 : 0.2;
  const edge = mix(base, [0, 0, 0], 0.32);
  for (let i = 0; i < n; i++) {
    const c = plan.cmds[i],
      x = plan.xs[i],
      y = plan.ys[i];
    if (c === STITCH) {
      const x0 = X(px),
        y0 = Y(py),
        x1 = X(x),
        y1 = Y(y);
      // 그림자 (원단에 드리운 실 그림자)
      ctx.strokeStyle = `rgba(0,0,0,${shadowA})`;
      ctx.lineWidth = thread * 1.12;
      ctx.beginPath();
      ctx.moveTo(x0 + off, y0 + off * 1.2);
      ctx.lineTo(x1 + off, y1 + off * 1.2);
      ctx.stroke();
      // 실 가장자리 → 몸통
      ctx.strokeStyle = edge;
      ctx.lineWidth = thread;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
      ctx.strokeStyle = body;
      ctx.lineWidth = thread * 0.8;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
      // 광택: 실 꼬임 방향으로 은은하게
      ctx.strokeStyle = hi;
      ctx.globalAlpha = 0.38;
      ctx.lineWidth = thread * 0.3;
      ctx.beginPath();
      ctx.moveTo(x0 - off * 0.6, y0 - off * 0.6);
      ctx.lineTo(x1 - off * 0.6, y1 - off * 0.6);
      ctx.stroke();
      ctx.globalAlpha = 1;
    } else if (c === JUMP && o.showJumps) {
      drawJump(ctx, X(px), Y(py), X(x), Y(y), view.scale);
    } else if (c === TRIM && o.showJumps) {
      ctx.fillStyle = "rgba(200,40,40,.9)";
      const r = Math.max(2, 0.25 * view.scale);
      ctx.fillRect(X(x) - r, Y(y) - r, r * 2, r * 2);
    }
    px = x;
    py = y;
  }
  ctx.restore();
}

function drawJump(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, scale: number) {
  ctx.save();
  ctx.strokeStyle = "rgba(90,90,90,.55)";
  ctx.lineWidth = Math.max(0.6, 0.05 * scale);
  ctx.setLineDash([Math.max(2, 0.4 * scale), Math.max(2, 0.4 * scale)]);
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();
  ctx.restore();
}

/** 원단 결: 아주 옅은 사선 줄무늬 (확대할수록 성기게) */
function drawWeave(ctx: CanvasRenderingContext2D, W: number, H: number, fabric: string, scale: number) {
  const rgb = hexToRgb(fabric);
  const dark = luminance(rgb) < 0.35;
  ctx.save();
  ctx.strokeStyle = dark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)";
  ctx.lineWidth = 1;
  const step = Math.max(3, 0.5 * scale);
  ctx.beginPath();
  for (let x = -H; x < W; x += step) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x + H, H);
  }
  ctx.stroke();
  ctx.restore();
}

/** 디자인 전체가 들어오는 보기 */
export function fitView(plan: PlanLike, W: number, H: number, pad = 24): View {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (let i = 0; i < plan.cmds.length; i++) {
    if (plan.cmds[i] !== STITCH) continue;
    const x = plan.xs[i],
      y = plan.ys[i];
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  }
  if (!isFinite(x0)) return { scale: 4, cx: W / 2, cy: H / 2 };
  const wmm = (x1 - x0) / 10 + 1,
    hmm = (y1 - y0) / 10 + 1;
  const scale = Math.max(0.5, Math.min((W - pad * 2) / wmm, (H - pad * 2) / hmm));
  const mx = (x0 + x1) / 20,
    my = (y0 + y1) / 20;
  return { scale, cx: W / 2 - mx * scale, cy: H / 2 - my * scale };
}
